import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Briefcase, Building2, FileMinus, FileText, Search, UserCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Hint, Kbd, ResultRow, SectionLabel } from './search-dialog-parts';
import type { FlatResult, SearchMeta, SearchResults } from './search-dialog-types';

export function SearchDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setQuery('');
      setSelectedIdx(0);
    } else {
      // Focus Input nach Mount
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const { data: response, isFetching } = useQuery({
    queryKey: ['search', query],
    queryFn: async () => api.get<SearchResults>('/search', { q: query }),
    enabled: query.trim().length >= 2,
    staleTime: 10_000,
  });
  const data = response?.data;
  const totals = response?.meta?.totals as SearchMeta['totals'] | undefined;

  const flat: FlatResult[] = [];
  if (data) {
    for (const c of data.customers) {
      flat.push({
        kind: 'customer',
        href: `/kunden/${c.nr}`,
        primary: c.name,
        secondary: `Kundennr. ${c.nr}${c.ort ? ` · ${c.ort}` : ''}`,
      });
    }
    for (const p of data.projects) {
      flat.push({
        kind: 'project',
        href: `/projekte/${p.id}`,
        primary: p.name,
        secondary: p.customer ? `${p.customer.name} (${p.customer.nr})` : '—',
        meta: p.status,
      });
    }
    for (const c of data.consultants) {
      flat.push({
        kind: 'consultant',
        href: `/consultants/${c.id}`,
        primary: c.name,
        secondary: c.role?.name ?? c.rolle ?? '—',
        meta: c.status,
      });
    }
    for (const i of data.invoices) {
      flat.push({
        kind: 'invoice',
        href: `/orderbuch/${encodeURIComponent(i.reNr)}`,
        primary: i.reNr,
        secondary: `${i.kunde} · ${formatDate(i.datum)}`,
        meta: formatCurrency(i.betrag),
      });
    }
    for (const g of data.gutschriften) {
      flat.push({
        kind: 'gutschrift',
        // BL-115/BL-218: kein eigener Detail-Pfad je Gutschrift — reine
        // Listen-Seite (kein :id-Route, Tab unter /orderbuch), Treffer
        // führen deshalb auf die Übersicht statt auf eine spezifische Zeile.
        href: '/orderbuch?tab=gutschriften',
        primary: g.nr ?? '(ohne Belegnummer)',
        secondary: `${g.aussteller ?? '—'}${g.datum ? ` · ${formatDate(g.datum)}` : ''}`,
        meta: g.brutto !== null ? formatCurrency(g.brutto) : undefined,
      });
    }
  }

  const totalCount = totals ? Object.values(totals).reduce((sum, n) => sum + n, 0) : flat.length;

  useEffect(() => {
    setSelectedIdx(0);
  }, [query, data]);

  const onSelect = (idx: number) => {
    const item = flat[idx];
    if (item) {
      navigate(item.href);
      onOpenChange(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      onSelect(selectedIdx);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl overflow-hidden p-0">
        <div className="flex items-center gap-3 border-b px-4 py-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Kunden, Projekte, Consultants, Rechnungen …"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
          />
          <kbd className="hidden items-center gap-0.5 rounded border border-hair bg-bg-deep px-1.5 py-0.5 text-xs text-muted-foreground sm:flex">
            ESC
          </kbd>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {query.trim().length < 2 && (
            <div className="p-6 text-center text-sm text-muted-foreground">
              Mindestens 2 Zeichen eingeben …
              <div className="mt-3 flex flex-wrap justify-center gap-2 text-xs">
                <Hint icon={Building2} label="Kunden" />
                <Hint icon={Briefcase} label="Projekte" />
                <Hint icon={UserCircle} label="Mitarbeiter" />
                <Hint icon={FileText} label="Rechnungen" />
                <Hint icon={FileMinus} label="Gutschriften" />
              </div>
            </div>
          )}
          {query.trim().length >= 2 && isFetching && (
            <p className="p-6 text-center text-sm text-muted-foreground">Wird gesucht …</p>
          )}
          {query.trim().length >= 2 && !isFetching && flat.length === 0 && (
            <p className="p-6 text-center text-sm text-muted-foreground">
              Keine Treffer für „{query}"
            </p>
          )}
          {flat.length > 0 && (
            <div className="py-1">
              {data?.customers && data.customers.length > 0 && <SectionLabel>Kunden</SectionLabel>}
              {flat.map((item, idx) => {
                const isFirstOfKind = idx === 0 || flat[idx - 1]!.kind !== item.kind;
                return (
                  <div key={`${item.kind}-${idx}`}>
                    {isFirstOfKind && idx > 0 && (
                      <SectionLabel>
                        {item.kind === 'customer' && 'Kunden'}
                        {item.kind === 'project' && 'Projekte'}
                        {item.kind === 'consultant' && 'Consultants'}
                        {item.kind === 'invoice' && 'Rechnungen'}
                        {item.kind === 'gutschrift' && 'Gutschriften'}
                      </SectionLabel>
                    )}
                    <ResultRow
                      item={item}
                      active={idx === selectedIdx}
                      onClick={() => onSelect(idx)}
                      onHover={() => setSelectedIdx(idx)}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t px-4 py-2 text-xs text-muted-foreground">
          <span>
            <Kbd>↑</Kbd> <Kbd>↓</Kbd> navigieren · <Kbd>↵</Kbd> auswählen
          </span>
          <span>
            {/* BL-115: `flat.length` ist pro Entität auf 5 gekappt — bei mehr
                echten Treffern zeigt das jetzt "5 von 12", nicht mehr fälschlich
                "5 Treffer" als vollständige Zahl. */}
            {flat.length > 0 &&
              (totalCount > flat.length
                ? `${flat.length} von ${totalCount} Treffern`
                : `${flat.length} Treffer`)}
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
